import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';

/** Per-text timings (ms). */
const TEXT_IN_MS = 320;
const TEXT_HOLD_MS = 1100;
const TEXT_OUT_MS = 280;
const ONE_TEXT_TOTAL_MS = TEXT_IN_MS + TEXT_HOLD_MS + TEXT_OUT_MS;

type Phase = 'character' | 'grade' | 'done';
type QueuePhase = Exclude<Phase, 'done'>;

export interface BadgeRevealSequenceProps {
  /** Character / subject of the card (e.g. "Robert Jopin"). Shown
   *  first. Skipped if empty/null. */
  character: string | null;
  /** Grading code (e.g. "PSA 10"). Shown second. Skipped if
   *  empty/null. */
  grade: string | null;
  /** Fires after the last text fades out — caller is expected to
   *  release the white-wipe (let it fade) and reveal WinnerStep. */
  onComplete: () => void;
  /** The lane is in a box of its own (a size container): the words
   *  scale with that box instead of the viewport. */
  contained?: boolean;
}

/**
 * Two-step badge reveal overlay — Figma 741:62374.
 *
 *   white screen (held by RevealGlowOverlay)
 *     → "Robert Jopin"  fade-in → hold → fade-out
 *     → "PSA 10"        fade-in → hold → fade-out
 *     → onComplete fires
 *
 * Each text is a single huge black headline centred on the white
 * pelt. Rendered above RevealGlowOverlay (`z-40` vs wipe's `z-30`).
 *
 * Empty fields are skipped — if `character` is null we go straight
 * to grade; if both are null `onComplete` fires next tick so the
 * caller can short-circuit the white-wipe hold.
 */
export function BadgeRevealSequence({
  character,
  grade,
  onComplete,
  contained = false,
}: BadgeRevealSequenceProps) {
  // Build the phase queue from the inputs — empty entries dropped
  // so we don't show a blank flicker between texts.
  const queueRef = useRef<QueuePhase[]>(
    (
      [
        character && character.length > 0 ? 'character' : null,
        grade && grade.length > 0 ? 'grade' : null,
      ] as const
    ).filter((p): p is QueuePhase => p !== null),
  );
  const [step, setStep] = useState<number>(0);
  // Latest-ref for `onComplete` so a re-render of the parent doesn't
  // restart the timer chain (would reset the sequence mid-play).
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (step >= queueRef.current.length) {
      onCompleteRef.current();
      return;
    }
    const t = window.setTimeout(() => {
      setStep((s) => s + 1);
    }, ONE_TEXT_TOTAL_MS);
    return () => window.clearTimeout(t);
  }, [step]);

  const currentPhase: Phase | null =
    step < queueRef.current.length ? queueRef.current[step]! : null;
  const text =
    currentPhase === 'character'
      ? character
      : currentPhase === 'grade'
        ? grade
        : null;

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center px-6"
    >
      <AnimatePresence mode="wait">
        {text && (
          <motion.span
            key={currentPhase ?? 'empty'}
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            // Per-property transition lets enter run on a soft
            // ease-out while exit fades quicker / harder so the
            // total cycle (in + hold + out) matches the per-text
            // timer above and the next entry lands seamlessly.
            exit={{
              opacity: 0,
              scale: 0.97,
              transition: { duration: TEXT_OUT_MS / 1000, ease: 'easeIn' },
            }}
            transition={{
              duration: TEXT_IN_MS / 1000,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="font-extrabold text-center tracking-[-0.028em]"
            style={{
              color: 'var(--co-BadgeRevealSequence-color, #15181E)',
              fontFamily: 'Inter, sans-serif',
              fontSize: contained ? 'clamp(56px, 14cqw, 170px)' : 'clamp(56px, 14vw, 170px)',
              lineHeight: 1.05,
            }}
          >
            {text}
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );
}
